import { useEffect } from 'react';

import { QueryClient } from '@tanstack/react-query';

import { NotificationResponse } from '@/api/data-contracts';
import { optimisticallyAddUnreadNotification } from '@/features/notifications';
import { queryKeys } from '@/react-query/constants';

import {
  invalidateOrderCommentQueries,
  isOrderCommentNotificationType,
} from './invalidateOrderCommentQueries';

interface UseNotificationsWebSocketParams {
  token: string | undefined;
  targetModule: string | undefined;
  queryClient: QueryClient;
}

const useNotificationsWebSocket = ({
  token,
  targetModule,
  queryClient,
}: UseNotificationsWebSocketParams) => {
  useEffect(() => {
    if (!token) return;
    if (typeof Stomp === 'undefined') return;

    try {
      // @ts-ignore
      const socket = new SockJS(import.meta.env.VITE_WS_URL);
      // @ts-ignore
      const stompClient = Stomp.over(socket);
      stompClient.debug = null;

      const bearerToken = `Bearer ${token}`;

      stompClient.connect({ Authorization: bearerToken }, () => {
        stompClient.subscribe(
          `/user/queue/notifications`,
          async (payload: { body?: string }) => {
            let isNewUnread = false;
            let countUpdatedOptimistically = false;
            let isServiceTaskEvent = false;
            let isOrderRequestedEvent = false;
            let isCommentEvent = false;

            if (payload?.body) {
              try {
                const notification = JSON.parse(payload.body) as NotificationResponse;
                isNewUnread = notification.status === 'UNREAD';
                isServiceTaskEvent =
                  typeof notification.audit?.type === 'string' &&
                  (notification.audit.type.startsWith('SERVICE_TASK') ||
                    notification.audit.type.startsWith('ORDER_BOOKING_DECORATED') ||
                    notification.audit.type.startsWith('ORDER_BOOKING_NEUTRALIZATION'));
                isOrderRequestedEvent = notification.audit?.type === 'ORDER_REQUESTED';
                isCommentEvent = isOrderCommentNotificationType(notification.audit?.type);
                countUpdatedOptimistically = optimisticallyAddUnreadNotification(
                  queryClient,
                  notification,
                  targetModule,
                );
              } catch {
                isNewUnread = true;
              }
            }

            if (isNewUnread) {
              setTimeout(() => {
                queryClient.invalidateQueries({ queryKey: [queryKeys.notifications, 'unread'] });
                queryClient.invalidateQueries({
                  queryKey: [queryKeys.notificationsCount],
                  refetchType: countUpdatedOptimistically ? 'none' : 'active',
                });
                queryClient.invalidateQueries({
                  queryKey: [queryKeys.bookingServiceTasksStatus],
                });
                if (isServiceTaskEvent) {
                  queryClient.invalidateQueries({ queryKey: [queryKeys.orders] });
                  queryClient.invalidateQueries({ queryKey: [queryKeys.mediaPlans] });
                  queryClient.invalidateQueries({ queryKey: [queryKeys.serviceOrders] });
                  queryClient.invalidateQueries({ queryKey: [queryKeys.serviceTaskFilters] });
                }
                if (isOrderRequestedEvent) {
                  queryClient.invalidateQueries({ queryKey: [queryKeys.orders, 'pending-count'] });
                }
                if (isCommentEvent) {
                  invalidateOrderCommentQueries(queryClient);
                }
              }, 500);
            }
          },
          { Authorization: bearerToken },
        );
      });

      return () => {
        if (stompClient && stompClient.connected) {
          stompClient.disconnect();
        }
      };
    } catch {
      // SockJS fails with SecurityError when connecting over HTTP from an HTTPS page — fall back to polling
    }
  }, [token, queryClient, targetModule]);
};

export default useNotificationsWebSocket;
